import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import GameCard from '../components/GameCard';
import LanguageSwitcher from '../components/LanguageSwitcher';
import games from '../games/manifest';
import { shuffleColors } from '../utils/pastelColors';
import { getGlobalStats, loadMaxPoints } from '../utils/useStreakPoints';
import { getAllStats } from '../utils/useGameHistory';
import { totalQuestions, questionCounts } from '../utils/questionCount';
import { DiceIcon, ChartIcon, StarIcon, CheckIcon, BookIcon, GearIcon } from '../utils/icons';
import { mechanicIcons } from '../utils/mechanicIcons';
import './Home.css';

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const shuffled = shuffleArray(games);
const cardColors = shuffleColors(shuffled.length);
const colorByGameId = Object.fromEntries(shuffled.map((g, i) => [g.id, cardColors[i]]));

const PAGE_SIZE = 6;

const MECHANICS = ['all', 'quiz', 'binary', 'type', 'unscramble', 'sort', 'match', 'sequence', 'swipe', 'hangman', 'memory', 'chain', 'puzzle'];

export default function Home() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [search, setSearch] = useState('');
  const [mechanic, setMechanic] = useState('all');
  const [page, setPage] = useState(1);
  const [verse, setVerse] = useState(null);
  const globalStats = getGlobalStats();
  const historyStats = getAllStats();
  const playedGames = loadMaxPoints();

  useEffect(() => {
    fetch('https://www.resursecrestine.ro/web-api-versetul-zilei/format/json')
      .then((r) => r.json())
      .then((data) => { if (data.verse && data.reference) setVerse(data); })
      .catch(() => {});
  }, []);

  const filtered = shuffled.filter((g) => {
    if (mechanic !== 'all' && g.mechanic !== mechanic) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return t(g.titleKey).toLowerCase().includes(q) || t(g.descriptionKey).toLowerCase().includes(q);
  });

  const isSearching = search.trim().length > 0;
  const visible = isSearching ? filtered : filtered.slice(0, page * PAGE_SIZE);
  const hasMore = !isSearching && filtered.length > visible.length;

  return (
    <div className="home-page">
      <header className="home-header">
        <div className="home-header-left">
          <h1>{t('home.header')}</h1>
          <p>{t('home.subtitle')}</p>
        </div>
        <div className="home-header-right">
          <input
            className="home-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`${t('home.search')} (${filtered.length})`}
          />
          <button className="home-random-btn tooltip-wrap" onClick={() => navigate('/random')}>
            <DiceIcon className="home-header-icon" />
            <span className="tooltip tooltip-bottom">{t('random.title')}</span>
          </button>
          <button className="home-random-btn tooltip-wrap" onClick={() => navigate('/progress')}>
            <ChartIcon className="home-header-icon" />
            <span className="tooltip tooltip-bottom">{t('progress.title')}</span>
          </button>
          <button className="home-random-btn tooltip-wrap" onClick={() => navigate('/settings')}>
            <GearIcon className="home-header-icon" />
            <span className="tooltip tooltip-bottom">{t('settings.title')}</span>
          </button>
          <LanguageSwitcher />
        </div>
      </header>
      <div className="home-container">
        {!isSearching && (
          <>
            <div className="home-global-stats">
              {globalStats.games > 0 && (
                <>
                  <span className="home-stat-item">
                    <StarIcon className="home-stat-icon" />
                    <span className="home-stat-text">{t('home.avgPoints', { n: globalStats.average })}</span>
                    <span className="home-stat-number">{globalStats.average}</span>
                  </span>
                  <span className="home-stat-item">
                    <DiceIcon className="home-stat-icon" />
                    <span className="home-stat-text">{t('home.gamesPlayed', { n: globalStats.games })}</span>
                    <span className="home-stat-number">{globalStats.games}</span>
                  </span>
                </>
              )}
              {historyStats.played > 0 && (
                <span className="home-stat-item">
                  <CheckIcon className="home-stat-icon" />
                  <span className="home-stat-text">{t('home.answered', { n: historyStats.played })}</span>
                  <span className="home-stat-number">{historyStats.played}</span>
                </span>
              )}
              <span className="home-stat-item">
                <BookIcon className="home-stat-icon" />
                <span className="home-stat-text">{t('home.totalQuestions', { n: totalQuestions })}</span>
                <span className="home-stat-number">{totalQuestions}</span>
              </span>
            </div>
            {verse && (
              <div className="home-verse">
                <p className="home-verse-text">"{verse.verse}"</p>
                <span className="home-verse-ref">— {verse.reference}</span>
              </div>
            )}
            <div className="home-filters">
              {MECHANICS.map((m) => (
                <button
                  key={m}
                  className={`home-filter-btn ${mechanic === m ? 'active' : ''}`}
                  onClick={() => { setMechanic(m); setPage(1); }}
                >
                  {mechanicIcons[m]}
                  {m === 'all' ? t('progress.allMechanics') : t(`mechanic.${m}`)}
                </button>
              ))}
            </div>
          </>
        )}
        <div key={mechanic} className={`games-grid ${isSearching ? 'games-grid-searching' : ''}`}>
          {visible.map((g, i) => (
            <GameCard
              key={g.id}
              style={{ animationDelay: `${Math.min(i, 11) * 0.04}s` }}
              title={t(g.titleKey)}
              description={t(g.descriptionKey)}
              icon={g.icon}
              color={colorByGameId[g.id]}
              questionCount={questionCounts[g.id]}
              mechanic={g.mechanic ? t(`mechanic.${g.mechanic}`) : null}
              mechanicKey={g.mechanic}
              unplayed={!playedGames[g.id]}
              onClick={() => navigate(`/game/${g.id}`)}
            />
          ))}
        </div>
        {hasMore && (
          <button className="home-show-more" onClick={() => setPage((p) => p + 1)}>
            {t('home.showMore', { n: filtered.length - visible.length })}
          </button>
        )}
      </div>
    </div>
  );
}
